import { URLConstructor } from '../../../../NonReact/utils/URLBuilder'
import { getQueryParam } from '../../../../NonReact/utils/LocationUtils'
import { FusionMode, FusionParentWindow } from './Types/Types'

/**
 * Property the new-UI parent window sets on its own window object before mounting the
 * CCS iframe. The CCS reads it from {@code window.parent[FUSION_PARENT_MODE_PROPERTY]}.
 */
export const FUSION_PARENT_MODE_PROPERTY = '__akixiFusionMode' as const

/**
 * CSS {@code display} value used to hide the CCS header chrome while still keeping the
 * element in the DOM (so layout-dependent JS does not break).
 */
export const DISPLAY_STYLE_NONE: string = 'none'

/**
 * URL query param read on direct/dev loads where no parent window is involved — lets us
 * activate fusion-legacy mode for ad-hoc testing without spinning up the new UI.
 */
export const FUSION_LEGACY_QUERY_PARAM: string = 'fusionLegacy'

/**
 * URL query param read on direct/dev loads where no parent window is involved — lets us
 * activate fusion-portal mode for ad-hoc testing without spinning up the new UI.
 */
export const FUSION_PORTAL_QUERY_PARAM: string = 'fusionPortal'

/** Value the fusion-legacy / fusion-portal query params must hold to activate the mode. */
export const FUSION_QUERY_VALUE_TRUE: string = 'true'

/**
 * Detect whether this page is running inside an iframe. All fusion-mode behaviour must
 * be suppressed when the CCS is opened directly (not embedded) so that a stale session
 * attribute or URL param never accidentally hides the header for a top-level user.
 */
export const IS_IN_IFRAME: boolean = ((): boolean => {
    try {
        return window.self !== window.top
    } catch {
        return true
    }
})()

/**
 * Read the fusion mode that the parent (new-UI) window exposed before the iframe loaded.
 * This is the most reliable signal: it survives server-side 302 redirects that strip URL
 * params, and is set synchronously before any CCS JS executes. Returns null when the
 * parent doesn't expose the property (cross-origin or direct load).
 */
const detectParentFusionMode = (): Nullable<FusionMode> => {
    try {
        if (window.parent !== window) {
            const parentWindow: FusionParentWindow = window.parent as FusionParentWindow

            return parentWindow[FUSION_PARENT_MODE_PROPERTY] ?? null
        }
    } catch {
        // Cross-origin parent cannot access this property. This should not happen in production.
    }

    return null
}

// Captured at module-load time so the value is available before any async settings fetch.
// All signals are gated on IS_IN_IFRAME so fusion mode never activates in a top-level tab.
export const PARENT_FUSION_MODE: Nullable<FusionMode> = IS_IN_IFRAME ? detectParentFusionMode() : null

export const IS_FUSION_LEGACY_INITIAL_URL: boolean =
    IS_IN_IFRAME &&
    (PARENT_FUSION_MODE === 'legacy' || getQueryParam(FUSION_LEGACY_QUERY_PARAM) === FUSION_QUERY_VALUE_TRUE)

export const IS_FUSION_PORTAL_INITIAL_URL: boolean =
    IS_IN_IFRAME &&
    (PARENT_FUSION_MODE === 'portal' || getQueryParam(FUSION_PORTAL_QUERY_PARAM) === FUSION_QUERY_VALUE_TRUE)

/**
 * Query-param name on the New UI target URL that carries the freshly-minted facade
 * session token. Read by the new UI on initial load to bootstrap its session.
 */
export const FUSION_TOKEN_QUERY_PARAM: string = 'fusionToken'

/**
 * CCS endpoint that performs the full New UI SSO exchange server-side: marks the
 * Cognito user as new-UI-enabled, generates a CCS JWT, and exchanges it with the
 * core-facade for a facade session token. The legacy front-end never contacts the
 * facade directly.
 *
 * Path-only constant kept for log/error messages and tests; the call sites that
 * issue the request must use {@link buildNewUiSsoUrl} so the {@link URLConstructor}
 * (and therefore the underlying {@code new URL()}) gets an absolute base.
 */
export const NEW_UI_SSO_PATH: string = '/CCS/API/v1/auth/new-ui-sso'

/**
 * Builds an absolute {@link URLConstructor} pointing at the {@link NEW_UI_SSO_PATH}.
 * Returning a fresh instance each call lets callers chain {@code appendQueryParams}
 * etc. without mutating shared state.
 */
export const buildNewUiSsoUrl = (): URLConstructor =>
    new URLConstructor({ baseURL: window.location.origin + NEW_UI_SSO_PATH })

/**
 * Local-storage key set when the user dismisses the "switch to the new UI" invite
 * toast (or completes the switch). Once present the toast never re-shows for that
 * browser profile.
 */
export const NEW_UI_INVITE_DISMISSED_STORAGE_KEY: string = 'akixi.newUiInviteDismissed'
